/**
 * Partida (item 4.5): escalação → pré-jogo → jogo (com HUD, minimapa e pausa) → resultado.
 * Os outros 13 atletas são bots parados ou os robôs simples do 4.3; os bots de verdade chegam no item 5.x.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MATCH, POSITIONS, type Position } from "@besenliga/shared";
import type { Team } from "@besenliga/sim";
import { browserStore, loadRoster, type SavedAthlete } from "../creator/roster";
import {
  Banner,
  ControlsHelp,
  END_REASON,
  Feed,
  Minimap,
  PilotPanel,
  Scoreboard,
  useFlightView,
} from "../flight/Hud";
import { CLUB_NAMES, clubsFor, otherClub, pilotFrom, type Pilot } from "../flight/pilot";
import { othersFor, type ResultLine } from "../flight/view";
import { lineLabels } from "./labels";

const ROLES: Position[] = ["artilheiro", "batedor", "cacador", "goleiro"];
const OPPONENTS = [
  { id: "parados", label: "Bots parados", hint: "Ficam no lugar e só passam a bola quando a recebem." },
  { id: "robos", label: "Robôs simples", hint: "Correm atrás da bola, arremessam e usam as habilidades." },
] as const;
type Opponents = (typeof OPPONENTS)[number]["id"];

type Step = "escalacao" | "pre-jogo" | "jogo" | "resultado";

export interface MatchSetup {
  athleteId: string | null;
  role: Position;
  opponents: Opponents;
}

const hex = (c: number) => `#${c.toString(16).padStart(6, "0")}`;

export function MatchScreen() {
  const roster = useMemo(() => loadRoster(browserStore()), []);
  const [setup, setSetup] = useState<MatchSetup>(() => ({
    athleteId: roster[0]?.id ?? null,
    role: roster[0]?.position ?? "artilheiro",
    opponents: "parados",
  }));
  const [step, setStep] = useState<Step>("escalacao");
  const [results, setResults] = useState<{
    lines: ResultLine[];
    hud: NonNullable<ReturnType<typeof snapshot>>;
  } | null>(null);
  const [round, setRound] = useState(0);
  const saved: SavedAthlete | undefined = roster.find((a) => a.id === setup.athleteId);
  const pilot = useMemo(() => pilotFrom(saved, setup.role), [saved, setup.role]);

  return (
    <div className="match-screen">
      {step === "escalacao" && (
        <Lineup
          roster={roster}
          setup={setup}
          onChange={setSetup}
          pilot={pilot}
          onNext={() => setStep("pre-jogo")}
        />
      )}
      {step === "pre-jogo" && (
        <PreGame
          pilot={pilot}
          setup={setup}
          onBack={() => setStep("escalacao")}
          onStart={() => setStep("jogo")}
        />
      )}
      {step === "jogo" && (
        <Game
          key={round}
          pilot={pilot}
          opponents={setup.opponents}
          onRestart={() => setRound((r) => r + 1)}
          onQuit={() => (location.hash = "#")}
          onEnd={(r) => {
            setResults(r);
            setStep("resultado");
          }}
        />
      )}
      {step === "resultado" && results && (
        <Result
          pilot={pilot}
          results={results}
          onAgain={() => {
            setRound((r) => r + 1);
            setStep("jogo");
          }}
          onLineup={() => setStep("escalacao")}
        />
      )}
    </div>
  );
}

function Lineup({
  roster,
  setup,
  onChange,
  pilot,
  onNext,
}: {
  roster: SavedAthlete[];
  setup: MatchSetup;
  onChange: (s: MatchSetup) => void;
  pilot: Pilot;
  onNext: () => void;
}) {
  return (
    <main className="menu-screen">
      <section className="menu-card wide" aria-labelledby="escalacao-titulo">
        <header className="menu-head">
          <a href="#" className="ghost">
            Menu
          </a>
          <h1 id="escalacao-titulo">Partida rápida</h1>
        </header>

        <fieldset className="menu-group">
          <legend>Seu atleta</legend>
          {roster.length === 0 ? (
            <p>
              Você vai jogar com o atleta de teste. <a href="#criar">Criar seu atleta</a>
            </p>
          ) : (
            <div className="menu-options">
              {roster.map((a) => (
                <label key={a.id} className="menu-option">
                  <input
                    type="radio"
                    name="atleta"
                    checked={setup.athleteId === a.id}
                    onChange={() => onChange({ ...setup, athleteId: a.id, role: a.position })}
                  />
                  <span>
                    <strong>{a.name}</strong> · {POSITIONS[a.position].label} · {CLUB_NAMES[a.kit].short}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="menu-group">
          <legend>Posição nesta partida</legend>
          <div className="segmented" role="group" aria-label="Posição">
            {ROLES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={setup.role === r}
                onClick={() => onChange({ ...setup, role: r })}
              >
                {POSITIONS[r].label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="menu-group">
          <legend>Adversários e companheiros</legend>
          <div className="menu-options">
            {OPPONENTS.map((o) => (
              <label key={o.id} className="menu-option">
                <input
                  type="radio"
                  name="adversarios"
                  checked={setup.opponents === o.id}
                  onChange={() => onChange({ ...setup, opponents: o.id })}
                />
                <span>
                  <strong>{o.label}</strong> · {o.hint}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="menu-actions row">
          <button type="button" className="primary" onClick={onNext}>
            Continuar com {pilot.name}
          </button>
        </div>
      </section>
    </main>
  );
}

function PreGame({
  pilot,
  setup,
  onBack,
  onStart,
}: {
  pilot: Pilot;
  setup: MatchSetup;
  onBack: () => void;
  onStart: () => void;
}) {
  const others = othersFor(setup.role);
  const away = otherClub(pilot.kitId);
  const side = (team: Team) => {
    const list: { label: string; role: Position; you?: boolean }[] =
      team === "casa" ? [{ label: pilot.name, role: setup.role, you: true }] : [];
    for (const o of others) if (o.team === team) list.push({ label: "Bot", role: o.pos });
    const order: Position[] = ["goleiro", "artilheiro", "batedor", "cacador"];
    return list.sort((a, b) => order.indexOf(a.role) - order.indexOf(b.role));
  };
  const club = { casa: pilot.kitId, fora: away } as const;
  return (
    <main className="menu-screen">
      <section className="menu-card wide pregame" aria-labelledby="prejogo-titulo">
        <h1 id="prejogo-titulo" className="pregame-vs">
          <span>{CLUB_NAMES[pilot.kitId].full}</span>
          <small>contra</small>
          <span>{CLUB_NAMES[away].full}</span>
        </h1>
        <div className="pregame-teams">
          {(["casa", "fora"] as const).map((team) => (
            <ul
              key={team}
              className="pregame-team"
              style={{ borderColor: hex(clubsFor(pilot.kitId)[team].kit.primary) }}
            >
              <li className="pregame-club">{CLUB_NAMES[club[team]].short}</li>
              {side(team).map((p, i) => (
                <li key={i} className={p.you ? "you" : undefined}>
                  <span>{POSITIONS[p.role].label}</span>
                  <strong>{p.you ? `${p.label} (você)` : p.label}</strong>
                </li>
              ))}
            </ul>
          ))}
        </div>
        <ul className="pregame-rules">
          <li>
            {MATCH.durationSeconds / 60} minutos de jogo; empate vai para {MATCH.suddenDeathSeconds} s de
            morte súbita
          </li>
          <li>
            Gol vale {MATCH.goalPoints}; a Faísca aparece aos {MATCH.sparkAppearsAtSeconds / 60} min, vale{" "}
            {MATCH.sparkCapturePoints} e encerra a partida
          </li>
          <li>Atropelo no turbo, bloquear o próprio aro e Petardo no goleiro na área dão pênalti</li>
          <li>
            <kbd>Esc</kbd> pausa a partida
          </li>
        </ul>
        <div className="menu-actions row">
          <button type="button" className="ghost" onClick={onBack}>
            Voltar
          </button>
          <button type="button" className="primary" onClick={onStart}>
            Entrar em campo
          </button>
        </div>
      </section>
    </main>
  );
}

const snapshot = (hud: ReturnType<typeof useFlightView>["hud"]) =>
  hud?.match ? { score: { ...hud.score }, match: { ...hud.match } } : null;

function Game({
  pilot,
  opponents,
  onRestart,
  onQuit,
  onEnd,
}: {
  pilot: Pilot;
  opponents: Opponents;
  onRestart: () => void;
  onQuit: () => void;
  onEnd: (r: { lines: ResultLine[]; hud: NonNullable<ReturnType<typeof snapshot>> }) => void;
}) {
  const holderRef = useRef<HTMLDivElement>(null);
  const clubs = useMemo(() => clubsFor(pilot.kitId), [pilot.kitId]);
  const kits = useMemo(() => ({ casa: clubs.casa.kit, fora: clubs.fora.kit }), [clubs]);
  const { state, hud, view } = useFlightView(holderRef, pilot, opponents, clubs);
  const [paused, setPaused] = useState(false);
  const ended = hud?.match?.phase === "fim";

  useEffect(() => {
    if (view) view.paused = paused;
  }, [view, paused]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Escape" && !ended) setPaused((p) => !p);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [ended]);

  const finish = () => {
    const lines = view?.results();
    const snap = snapshot(hud);
    if (lines && snap) onEnd({ lines, hud: snap });
  };

  // Fim de jogo: a faixa aparece por um instante e o resultado abre sozinho.
  useEffect(() => {
    if (!ended) return;
    const id = setTimeout(finish, 3500);
    return () => clearTimeout(id);
  }, [ended]);

  return (
    <div className="stadium-screen flight-screen" data-state={state}>
      <div ref={holderRef} className="stadium-holder" />
      <header className="stadium-bar">
        <button type="button" className="ghost" onClick={() => setPaused(true)} disabled={ended}>
          Pausar
        </button>
        <h1>Partida</h1>
      </header>
      {hud && <Scoreboard hud={hud} clubs={clubs} />}
      <Minimap view={view} kits={kits} />
      {hud && <Feed hud={hud} />}
      {hud && !paused && (
        <Banner hud={hud} clubs={clubs}>
          {ended && (
            <button type="button" className="primary" onClick={finish}>
              Ver resultado
            </button>
          )}
        </Banner>
      )}
      {hud && <PilotPanel hud={hud} name={pilot.name} role={pilot.role} mode={opponents} />}
      <ControlsHelp />
      {paused && (
        <div className="pause-screen" role="dialog" aria-modal="true" aria-labelledby="pausa-titulo">
          <div className="menu-card">
            <h2 id="pausa-titulo">Partida pausada</h2>
            <div className="menu-actions">
              <button type="button" className="primary" onClick={() => setPaused(false)}>
                Continuar
              </button>
              <button type="button" className="ghost" onClick={onRestart}>
                Recomeçar a partida
              </button>
              <button type="button" className="ghost" onClick={onQuit}>
                Sair para o menu
              </button>
            </div>
          </div>
        </div>
      )}
      {state === "erro" && (
        <p className="stage-message" role="alert">
          Não foi possível mostrar a partida em 3D neste navegador.
        </p>
      )}
    </div>
  );
}

const STAT_COLUMNS = [
  { key: "gols", label: "Gols" },
  { key: "passesCertos", label: "Passes certos" },
  { key: "roubos", label: "Roubos" },
  { key: "defesas", label: "Defesas" },
  { key: "petardosAcertados", label: "Petardos acertados" },
  { key: "capturas", label: "Faísca" },
  { key: "faltas", label: "Faltas" },
] as const;

function Result({
  pilot,
  results,
  onAgain,
  onLineup,
}: {
  pilot: Pilot;
  results: { lines: ResultLine[]; hud: NonNullable<ReturnType<typeof snapshot>> };
  onAgain: () => void;
  onLineup: () => void;
}) {
  const clubs = clubsFor(pilot.kitId);
  const { score, match } = results.hud;
  const labels = lineLabels(results.lines, pilot.name);
  const you = results.lines.find((l) => l.you);
  const best = [...results.lines].sort((a, b) => b.rating - a.rating)[0];
  const outcome =
    match.winner === "empate" || !match.winner ? "Empate" : match.winner === "casa" ? "Vitória" : "Derrota";
  return (
    <main className="menu-screen">
      <section className="menu-card wide result" aria-labelledby="resultado-titulo">
        <p className="result-outcome" data-outcome={outcome}>
          {outcome}
        </p>
        <h1 id="resultado-titulo" className="result-score">
          <span style={{ background: hex(clubs.casa.kit.primary) }}>
            {clubs.casa.name} {score.casa}
          </span>
          <span style={{ background: hex(clubs.fora.kit.primary) }}>
            {score.fora} {clubs.fora.name}
          </span>
        </h1>
        <p className="result-reason">{END_REASON[match.endReason ?? "tempo"]}</p>
        {you && (
          <p className="result-you">
            Sua nota: <strong>{you.rating.toFixed(1)}</strong>
            {best && (
              <>
                {" "}
                · Melhor em campo: <strong>{labels[best.id]}</strong> ({best.rating.toFixed(1)})
              </>
            )}
          </p>
        )}
        {(["casa", "fora"] as const).map((team) => (
          <table key={team} className="result-table">
            <caption style={{ color: hex(clubs[team].kit.primary) }}>{clubs[team].name}</caption>
            <thead>
              <tr>
                <th scope="col">Atleta</th>
                {STAT_COLUMNS.map((c) => (
                  <th key={c.key} scope="col">
                    {c.label}
                  </th>
                ))}
                <th scope="col">Nota</th>
              </tr>
            </thead>
            <tbody>
              {results.lines
                .filter((l) => l.team === team)
                .map((l) => (
                  <tr key={l.id} className={l.you ? "you" : undefined}>
                    <th scope="row">{labels[l.id]}</th>
                    {STAT_COLUMNS.map((c) => (
                      <td key={c.key}>{l.stats[c.key]}</td>
                    ))}
                    <td className="rating">{l.rating.toFixed(1)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        ))}
        <div className="menu-actions row">
          <button type="button" className="primary" onClick={onAgain}>
            Jogar de novo
          </button>
          <button type="button" className="ghost" onClick={onLineup}>
            Mudar escalação
          </button>
          <a className="ghost" href="#">
            Menu
          </a>
        </div>
      </section>
    </main>
  );
}
